import React, { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import ConfirmPopup from '@components/ConfirmPopup'
import CustomPopover from '@components/CustomPopover'
import { AlertFrequencyType } from '@modules/alerts/types'
import { FormControl, InputLabel, MenuItem, SelectChangeEvent, Typography } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import { useScreenDimensions } from '@shared/hooks/useScreenDimensions'
import { colors } from '@theme/variables'

import { EditAlertButtonWrapper, FrequencySelect, ProfileAlertPopoverWrapper, StyledProfileAlertButton } from '../index.styles'

interface ProfileAlertPopoverProps {
  onSave: (frequency?: AlertFrequencyType) => void
  onSelect?: (frequency?: AlertFrequencyType) => void
  onDelete?: () => void
  onCancel?: () => void
  isEdit: boolean
  preselectedAlertFrequency?: AlertFrequencyType
  anchor: Nullable<HTMLElement>
}

const ProfileAlertPopover = ({
  onSave,
  onCancel,
  onSelect,
  anchor,
  isEdit,
  preselectedAlertFrequency = AlertFrequencyType.NO_NOTIFICATION,
  onDelete,
}: ProfileAlertPopoverProps) => {
  const { t } = useTranslation()
  const { isDesktop } = useScreenDimensions()

  const [selectedAlertFrequency, setSelectedAlertFrequncy] = useState<AlertFrequencyType>(preselectedAlertFrequency)
  const [showConfirmPopup, setShowConfirmPopup] = useState(false)

  const handleClose = useCallback(() => {
    if (selectedAlertFrequency !== preselectedAlertFrequency) {
      setShowConfirmPopup(true)
    } else {
      onCancel?.()
    }
    // eslint-disable-next-line
  }, [selectedAlertFrequency, preselectedAlertFrequency])

  const [title, selectTitle] = useMemo(
    () =>
      isEdit
        ? [t(translationKeys.EDIT_EMAIL_ALERTS_TITLE), t(translationKeys.EDIT_EMAIL_ALERTS_SUBTITLE)]
        : [t(translationKeys.ADD_EMAIL_ALERTS_TITLE), t(translationKeys.ADD_EMAIL_ALERTS_TITLE)],
    [isEdit, t],
  )

  const InnerContent = (
    <ProfileAlertPopoverWrapper>
      <Typography variant="h2" data-testid="title">
        {title}
      </Typography>

      <FormControl variant="outlined" fullWidth>
        <InputLabel
          sx={{
            background: colors.white,
            padding: '0 6px',
            marginTop: '2px',
            color: '#00000099',
          }}
          id="profile-alert-select"
        >
          {selectTitle}
        </InputLabel>
        <FrequencySelect
          data-testid="frequency-select"
          id="profile-alert-select"
          value={selectedAlertFrequency}
          labelId="profile-alert-select"
          onChange={(e: SelectChangeEvent<unknown>) => {
            const value = e.target.value as AlertFrequencyType
            onSelect?.(value)
            setSelectedAlertFrequncy(value)
          }}
        >
          <MenuItem value={AlertFrequencyType.NO_NOTIFICATION}>{t(translationKeys.ALERT_FREQUENCY_NO_NOTIFICATION)}</MenuItem>
          <MenuItem value={AlertFrequencyType.DAILY}>{t(translationKeys.ALERT_FREQUENCY_DAILY)}</MenuItem>
          <MenuItem value={AlertFrequencyType.WEEKLY}>{t(translationKeys.ALERT_FREQUENCY_WEEKLY)}</MenuItem>
        </FrequencySelect>
      </FormControl>

      <EditAlertButtonWrapper>
        {isEdit ? (
          <StyledProfileAlertButton data-testid="delete-button" onClick={() => onDelete?.()} variant="outlined" size="small">
            {t(translationKeys.REMOVE_ALL)}
          </StyledProfileAlertButton>
        ) : (
          <StyledProfileAlertButton data-testid="cancel-button" onClick={handleClose} variant="outlined" size="small">
            {t(translationKeys.CANCEL_BUTTON)}
          </StyledProfileAlertButton>
        )}
        <StyledProfileAlertButton
          data-testid="save-button"
          onClick={() => onSave(selectedAlertFrequency)}
          variant="contained"
          size="small"
        >
          {t(translationKeys.SAVE)}
        </StyledProfileAlertButton>
      </EditAlertButtonWrapper>
    </ProfileAlertPopoverWrapper>
  )

  if (!isDesktop) {
    return (
      <>
        <Dialog open={Boolean(anchor)} onClose={handleClose} fullWidth maxWidth="xs">
          <DialogContent>{InnerContent}</DialogContent>
        </Dialog>

        {showConfirmPopup && (
          <ConfirmPopup
            id="profile-alert-confirm-exit-popup"
            width={294}
            contentWidth={180}
            isOpen
            title={t(translationKeys.UNSAVED_SETTINGS)}
            text={t(translationKeys.YOU_HAVENOT_SAVED_CHANGES)}
            cancelBtnText={t(translationKeys.CONTINUE_EDITING)}
            confirmBtnText={t(translationKeys.CLOSE_ANYWAY)}
            onCancel={() => setShowConfirmPopup(false)}
            onConfirm={() => {
              setShowConfirmPopup(false)
              onCancel?.()
            }}
          />
        )}
      </>
    )
  }

  return (
    <>
      <CustomPopover
        sx={{ marginTop: '10px' }}
        id="profile-alert-set-notification"
        anchor={anchor}
        setAnchor={handleClose}
        onClose={handleClose}
      >
        {InnerContent}
      </CustomPopover>

      {showConfirmPopup && (
        <ConfirmPopup
          id="profile-alert-confirm-exit-popup"
          width={294}
          contentWidth={180}
          isOpen
          title={t(translationKeys.UNSAVED_SETTINGS)}
          text={t(translationKeys.YOU_HAVENOT_SAVED_CHANGES)}
          cancelBtnText={t(translationKeys.CONTINUE_EDITING)}
          confirmBtnText={t(translationKeys.CLOSE_ANYWAY)}
          onCancel={() => setShowConfirmPopup(false)}
          onConfirm={() => {
            setShowConfirmPopup(false)
            onCancel?.()
          }}
        />
      )}
    </>
  )
}

export default memo(ProfileAlertPopover)
